import { FieldDefinition, FIELD_PATH_SEPARATOR } from '../types/FieldDefinition';
import { FieldMappingEntry } from '../types/DataSyncViewModel';
import {
  ValidationType,
  MappingValidation,
  MappingTarget,
  VALIDATION_MSG_INCOMPATIBLE_TYPE,
  typeMapping,
} from '../types/FieldMapping';
import { findFieldByPath } from '../lib/dataSync';
import { FieldMappingTreeNode } from './fieldMappingTreeBuilder';

/**
 * Type guard to check if a field has array fields
 */
const hasArrayFields = (
  field: FieldDefinition
): field is FieldDefinition & { array: { fields: FieldDefinition[] } } => {
  return (
    field.array !== undefined &&
    typeof field.array === 'object' &&
    'fields' in field.array
  );
};

/**
 * Navigates to child fields of a field (object schema or array fields)
 * Returns the child fields array or undefined if no children
 */
const navigateToChildFields = (field: FieldDefinition): FieldDefinition[] | undefined => {
  if (field.object_schema?.fields) {
    return field.object_schema.fields;
  } else if (hasArrayFields(field)) {
    return field.array.fields;
  }
  return undefined;
};

/**
 * Gets the type of a field definition
 */
const getFieldType = (field: FieldDefinition): string => {
  if (field.type) {
    return field.type.toLowerCase();
  } else if (field.primitive_type) {
    return field.primitive_type.toLowerCase();
  } else if (field.object_schema) {
    return 'object';
  } else if (field.array) {
    return 'array';
  }
  return 'unknown';
};

/**
 * Analyzes a field path to determine nesting level, array presence, etc.
 * This is the primary function for path analysis - use this instead of separate
 * countArraysInPath or getArrayNamesFromPath functions.
 */
const analyzeFieldPath = (path: string, fields: FieldDefinition[]) => {
  const pathParts = path.split(FIELD_PATH_SEPARATOR);
  let hasArrayInPath = false;
  let isWithinArray = false;
  let arrayCount = 0;
  const arrayIndices: number[] = [];
  const arrayNames: string[] = [];
  const level = pathParts.length - 1;
  let currentFields = fields;

  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    const field = currentFields.find((f) => f.name === part);

    if (!field) {
      break;
    }

    const fieldType = getFieldType(field);

    if (fieldType === 'array') {
      hasArrayInPath = true;
      arrayCount++;
      arrayIndices.push(i);
      arrayNames.push(field.name);
      if (i < pathParts.length - 1) {
        isWithinArray = true;
      }
    }

    // Navigate to nested fields using helper
    const childFields = navigateToChildFields(field);
    if (childFields) {
      currentFields = childFields;
    }
  }

  const result = {
    level,
    hasArrayInPath,
    isWithinArray,
    arrayCount,
    arrayIndices,
    arrayNames,
  };

  return result;
};

/**
 * Validates mapping conditions according to the business rules
 */
const validateMappingConditions = (
  sourcePath: string,
  destinationPath: string,
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[],
): MappingValidation | undefined => {
  const sourceAnalysis = analyzeFieldPath(sourcePath, sourceFields);
  const destinationAnalysis = analyzeFieldPath(
    destinationPath,
    destinationFields
  );

  if (sourceAnalysis.hasArrayInPath !== destinationAnalysis.hasArrayInPath) {
    if (sourceAnalysis.hasArrayInPath) {
      return {
        name: sourcePath,
        type: ValidationType.ERROR,
        target: MappingTarget.SOURCE,
        message: 'Array fields can only be mapped to other array fields.',
      };
    } else {
      return {
        name: sourcePath,
        type: ValidationType.ERROR,
        target: MappingTarget.SOURCE,
        message:
          'Root level fields or fields within objects can not be mapped to array field.',
      };
    }
  }

  // Check if array counts match when both have arrays
  if (
    sourceAnalysis.hasArrayInPath &&
    destinationAnalysis.hasArrayInPath &&
    sourceAnalysis.arrayCount !== destinationAnalysis.arrayCount
  ) {
    return {
      name: sourcePath,
      type: ValidationType.ERROR,
      target: MappingTarget.SOURCE,
      message:
        `Source (${sourceAnalysis.arrayCount}) and destination ` +
        `(${destinationAnalysis.arrayCount}) array counts must match.`,
    };
  }

  return undefined;
};

/**
 * Validates a field mapping between source and destination fields
 * Checks for field existence, type compatibility, and array rules
 *
 * @param source - Source field path
 * @param destination - Destination field path
 * @param sourceFields - All available source field definitions
 * @param destinationFields - All available destination field definitions
 * @returns MappingValidation object if there's an error or warning, undefined if valid
 */
export const getFieldMappingValidations = ({
  source,
  destination,
  sourceFields,
  destinationFields,
}: {
  source: string;
  destination: string;
  sourceFields: FieldDefinition[];
  destinationFields: FieldDefinition[];
}): MappingValidation | undefined => {
  const sourceField = findFieldByPath(sourceFields, source);
  const destinationField = destinationFields
    ? findFieldByPath(destinationFields, destination)
    : undefined;

  if (!sourceField) {
    return {
      name: source,
      type: ValidationType.ERROR,
      target: MappingTarget.SOURCE,
      message: `Field "${source}" does not exist in source schema. Mapping needs to be updated.`,
    };
  }
  if (!destinationField) {
    return {
      name: destination,
      type: ValidationType.ERROR,
      target: MappingTarget.DESTINATION,
      message: `Field "${destination}" does not exist in destination schema. Mapping needs to be updated.`,
    };
  }

  // Apply mapping condition validations if both fields exist
  if (sourceField && destinationField) {
    const sourceType = getFieldType(sourceField);
    const destinationType = getFieldType(destinationField);

    // Apply advanced mapping condition validations
    const conditionalValidation = validateMappingConditions(
      source,
      destination,
      sourceFields,
      destinationFields,
    );

    if (conditionalValidation) {
      return conditionalValidation;
    }

    // Basic type compatibility check
    if (
      typeMapping[destinationType] &&
      !typeMapping[destinationType].includes(sourceType)
    ) {
      return {
        name: source,
        type: ValidationType.WARNING,
        target: MappingTarget.SOURCE,
        message: VALIDATION_MSG_INCOMPATIBLE_TYPE,
      };
    }
  }

  return undefined;
};

/**
 * Validation result for a source field against a destination field
 */
export interface SourceFieldValidationResult {
  /** Whether this source field is valid for the destination */
  isValid: boolean;
  /** Validation type if invalid (ERROR or WARNING) */
  validationType?: ValidationType;
  /** Human-readable message explaining why the field is invalid */
  message?: string;
}

/**
 * Determines which source arrays correspond to destination arrays
 * by analyzing existing sibling mappings
 */
const getArrayCorrespondence = (
  destinationPath: string,
  existingMappings: FieldMappingEntry[],
  destinationFields: FieldDefinition[],
  sourceFields: FieldDefinition[]
): Map<string, string> => {
  const correspondence = new Map<string, string>();

  // Get arrays in the destination path we're validating
  const destAnalysis = analyzeFieldPath(destinationPath, destinationFields);
  const destArrays = destAnalysis.arrayNames;

  if (destArrays.length === 0) {
    return correspondence;
  }

  // Look for existing mappings that share the same array parent structure
  for (const mapping of existingMappings) {
    if (!mapping.sourceField || !mapping.destinationField) {
      continue;
    }

    const mappingDestAnalysis = analyzeFieldPath(mapping.destinationField, destinationFields);
    const mappingSourceAnalysis = analyzeFieldPath(mapping.sourceField, sourceFields);
    const mappingDestArrays = mappingDestAnalysis.arrayNames;
    const mappingSourceArrays = mappingSourceAnalysis.arrayNames;

    // Check if this mapping shares array structure with our destination path
    let sharesStructure = true;
    for (let i = 0; i < Math.min(destArrays.length, mappingDestArrays.length); i++) {
      if (destArrays[i] !== mappingDestArrays[i]) {
        sharesStructure = false;
        break;
      }
    }

    if (sharesStructure && mappingDestArrays.length > 0) {
      // This mapping shares array structure, extract correspondences
      for (let i = 0; i < Math.min(mappingDestArrays.length, mappingSourceArrays.length); i++) {
        const destArray = mappingDestArrays[i];
        const sourceArray = mappingSourceArrays[i];

        // Only set if not already set, first mapping wins
        if (!correspondence.has(destArray)) {
          correspondence.set(destArray, sourceArray);
        }
      }
    }
  }

  return correspondence;
};

/**
 * Collects all leaf field paths from a field definition tree
 * A leaf field is one that can actually be mapped (not an object or array parent)
 */
const collectLeafFieldPaths = (
  fields: FieldDefinition[],
  parentPath: string = ''
): string[] => {
  const leafPaths: string[] = [];

  for (const field of fields) {
    const fieldPath = parentPath ? `${parentPath}${FIELD_PATH_SEPARATOR}${field.name}` : field.name;

    // Navigate to child fields using helper
    const childFields = navigateToChildFields(field);

    if (!childFields || childFields.length === 0) {
      // This is a leaf field (no children)
      leafPaths.push(fieldPath);
    } else {
      // Recursively collect from nested fields
      leafPaths.push(...collectLeafFieldPaths(childFields, fieldPath));
    }
  }

  return leafPaths;
};

/**
 * Validates all source fields against a specific destination field
 * Returns a map of source field paths to validation results
 *
 * This function is UI-agnostic and can be used for:
 * - Building disabled fields map in UI dialogs
 * - Auto-mapping logic to find compatible source fields
 * - Validation checks before saving mappings
 *
 * @param destinationFieldPath - The destination field path to validate against
 * @param sourceFields - All available source field definitions
 * @param destinationFields - All available destination field definitions
 * @param existingMappings - Existing field mappings (used for array correspondence validation)
 * @returns Map of source field paths to validation results
 */
export const validateSourceFieldsForDestination = (
  destinationFieldPath: string,
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[],
  existingMappings: FieldMappingEntry[]
): Map<string, SourceFieldValidationResult> => {
  const validationResults = new Map<string, SourceFieldValidationResult>();

  // Analyze destination path once
  const destAnalysis = analyzeFieldPath(destinationFieldPath, destinationFields);
  const destArrayCount = destAnalysis.arrayCount;
  const destArrayNames = destAnalysis.arrayNames;

  // Get array correspondence from existing mappings
  const arrayCorrespondence = getArrayCorrespondence(
    destinationFieldPath,
    existingMappings,
    destinationFields,
    sourceFields
  );

  // Get all leaf source field paths (only leaf fields can be mapped)
  const sourceFieldPaths = collectLeafFieldPaths(sourceFields);

  // Validate each source field path
  for (const sourceFieldPath of sourceFieldPaths) {
    // Analyze source path once
    const sourceAnalysis = analyzeFieldPath(sourceFieldPath, sourceFields);
    const sourceArrayCount = sourceAnalysis.arrayCount;
    const sourceArrayNames = sourceAnalysis.arrayNames;

    // Validation #1: Array count must match
    if (sourceArrayCount !== destArrayCount) {
      validationResults.set(sourceFieldPath, {
        isValid: false,
        validationType: ValidationType.ERROR,
        message:
          `Array counts mismatch: destination has ${destArrayCount} array(s), ` +
          `but this source field has ${sourceArrayCount} array(s).`,
      });
      continue;
    }

    // Validation #2: Array name consistency based on existing mappings
    // If we have established array correspondences, source arrays must match
    if (arrayCorrespondence.size > 0) {
      let arrayMismatch = false;
      let mismatchMessage = '';

      // Check each destination array has the correct corresponding source array
      for (let i = 0; i < destArrayNames.length; i++) {
        const destArrayName = destArrayNames[i];
        const expectedSourceArrayName = arrayCorrespondence.get(destArrayName);

        if (expectedSourceArrayName) {
          // We have an established correspondence for this array
          const actualSourceArrayName = sourceArrayNames[i];

          if (actualSourceArrayName !== expectedSourceArrayName) {
            // Source array at this position doesn't match expected
            arrayMismatch = true;
            mismatchMessage =
              `Array mixing not allowed: destination array "${destArrayName}" is already ` +
              `mapped to source array "${expectedSourceArrayName}".`;
            break;
          }
        }
      }

      if (arrayMismatch) {
        validationResults.set(sourceFieldPath, {
          isValid: false,
          validationType: ValidationType.ERROR,
          message: mismatchMessage,
        });
        continue;
      }
    }

    // Validation #3: Type compatibility and other validations from getFieldMappingValidations
    const validation = getFieldMappingValidations({
      source: sourceFieldPath,
      destination: destinationFieldPath,
      sourceFields,
      destinationFields,
    });

    if (validation && validation.type === ValidationType.ERROR) {
      validationResults.set(sourceFieldPath, {
        isValid: false,
        validationType: ValidationType.ERROR,
        message: validation.message,
      });
      continue;
    }

    if (validation && validation.type === ValidationType.WARNING) {
      // WARNINGs should not disable fields in picker, but should be visible in table cell
      validationResults.set(sourceFieldPath, {
        isValid: true,  // Still selectable
        validationType: ValidationType.WARNING,
        message: validation.message,
      });
      continue;
    }

    // If we got here, the source field is valid
    validationResults.set(sourceFieldPath, {
      isValid: true,
    });
  }

  return validationResults;
};

/**
 * Helper function to get only the invalid source fields for a destination
 * Useful for building disabled fields maps in UI components
 *
 * @param destinationFieldPath - The destination field path to validate against
 * @param sourceFields - All available source field definitions
 * @param destinationFields - All available destination field definitions
 * @param existingMappings - Existing field mappings
 * @returns Map of invalid source field paths to error messages
 */
export const getInvalidSourceFields = (
  destinationFieldPath: string,
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[],
  existingMappings: FieldMappingEntry[]
): Map<string, string> => {
  const allValidations = validateSourceFieldsForDestination(
    destinationFieldPath,
    sourceFields,
    destinationFields,
    existingMappings
  );

  const invalidFields = new Map<string, string>();

  allValidations.forEach((result, sourceFieldPath) => {
    if (!result.isValid && result.message) {
      invalidFields.set(sourceFieldPath, result.message);
    }
  });

  return invalidFields;
};

/**
 * Helper function to get only the valid source fields for a destination
 * Useful for auto-mapping logic to find compatible fields
 *
 * @param destinationFieldPath - The destination field path to validate against
 * @param sourceFields - All available source field definitions
 * @param destinationFields - All available destination field definitions
 * @param existingMappings - Existing field mappings
 * @returns Array of valid source field paths
 */
export const getValidSourceFields = (
  destinationFieldPath: string,
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[],
  existingMappings: FieldMappingEntry[]
): string[] => {
  const allValidations = validateSourceFieldsForDestination(
    destinationFieldPath,
    sourceFields,
    destinationFields,
    existingMappings
  );

  const validFields: string[] = [];

  allValidations.forEach((result, sourceFieldPath) => {
    if (result.isValid) {
      validFields.push(sourceFieldPath);
    }
  });

  return validFields;
};

/**
 * Validation result for UI display
 */
export interface ValidationResult {
  type: ValidationType;
  message: string;
}

/**
 * Summary of validation statistics
 */
export interface ValidationSummary {
  errorCount: number;
  warningCount: number;
  unmappedRequiredCount: number;
}

/**
 * Builds a validation map for all field mappings
 * @param mappings - Array of field mapping entries
 * @param sourceFields - Source field definitions
 * @param destinationFields - Destination field definitions
 * @returns Map of destination field paths to validation results
 */
export const buildValidationMap = (
  mappings: FieldMappingEntry[],
  sourceFields: FieldDefinition[],
  destinationFields: FieldDefinition[]
): Map<string, ValidationResult> => {
  const validations = new Map<string, ValidationResult>();

  // Check each destination field's source mapping for errors and warnings
  mappings.forEach((mapping) => {
    if (mapping.sourceField && mapping.destinationField && !mapping.disabled) {
      // Skip mappings whose fields no longer exist in the current schema.
      // This prevents stale mappings (from a previous source/destination) from producing
      // hidden validation errors that block saving but aren't visible in the UI.
      const destinationField = findFieldByPath(destinationFields, mapping.destinationField);
      if (!destinationField) {
        return;
      }

      const sourceField = findFieldByPath(sourceFields, mapping.sourceField);
      if (!sourceField) {
        validations.set(mapping.destinationField, {
          type: ValidationType.ERROR,
          message: 'Field does not exist in the schema. Mapping needs to be updated.',
        });
        return;
      }

      // Then run full validation to catch warnings (type mismatches, etc.)
      const validation = getFieldMappingValidations({
        source: mapping.sourceField,
        destination: mapping.destinationField,
        sourceFields,
        destinationFields
      });

      if (validation &&
        (validation.type === ValidationType.ERROR || validation.type === ValidationType.WARNING)) {
        validations.set(mapping.destinationField, {
          type: validation.type,
          message: validation.message,
        });
      }
    }
  });

  return validations;
};

/**
 * Calculates validation summary statistics
 * @param validations - Map of validation results
 * @param treeData - Tree structure of destination fields
 * @returns Summary with counts of errors, warnings, and unmapped required fields
 */
export const calculateValidationSummary = (
  validations: Map<string, ValidationResult>,
  treeData: FieldMappingTreeNode[]
): ValidationSummary => {
  let errorCount = 0;
  let warningCount = 0;
  let unmappedRequiredCount = 0;

  // Count validation errors and warnings
  validations.forEach((validation) => {
    if (validation.type === ValidationType.ERROR) {
      errorCount++;
    } else if (validation.type === ValidationType.WARNING) {
      warningCount++;
    }
  });

  // Count unmapped required fields by traversing the tree
  const countUnmappedRequired = (nodes: FieldMappingTreeNode[]) => {
    nodes.forEach((node) => {
      if (node.isLeaf && node.required && !node.sourceField) {
        unmappedRequiredCount++;
      }
      if (node.children.length > 0) {
        countUnmappedRequired(node.children);
      }
    });
  };
  countUnmappedRequired(treeData);

  return { errorCount, warningCount, unmappedRequiredCount };
};
